// =============================================================================
//  informeDeEjercicios.js
//  TODO EL TRABAJO DE UN VÍDEO, ESCRITO EN TEXTO PLANO
//
//  Rob, 05-10-2026: «un botón de copiar todo de todas las secciones y sus
//  ejercicios, algo que en un solo clic me traiga todo el ejercicio completo con
//  su transcript para validar con otra IA y ver si está correcto».
//
//  ── POR QUÉ ESTO VIVE EN SU PROPIO FICHERO ──────────────────────────────────
//  Porque es la única parte de esta función que puede estar MAL sin que se note.
//  Un botón que no copia se ve al primer clic; un informe al que le falta la
//  explicación de un ejercicio, o que marca la opción correcta en el sitio
//  equivocado, pasa la revisión de la otra IA con un «todo bien» que no vale
//  nada — y eso es peor que no tener el botón.
//
//  Al ser una función suelta que recibe datos y devuelve texto, se puede medir
//  entera sin navegador: `audita_informe_ejercicios.mjs`.
//
//  ── LA REGLA ────────────────────────────────────────────────────────────────
//  Lo que no esté, se DICE que no está. Nunca se calla un hueco: un ejercicio
//  sin generar sale como «(sin generar)» y una explicación vacía como «(sin
//  explicación)». Si el informe omitiera en silencio lo que falta, el revisor
//  estaría aprobando un vídeo a medias creyendo que lo ha visto entero.
// =============================================================================

const LETRAS = 'ABCDEFGHIJ';

//  Lo que se le pide al que revise. Va dentro del propio texto a propósito: lo
//  que se pega en otra IA es una sola cosa, y si las instrucciones hubiera que
//  escribirlas aparte, antes o después se pegaría el informe a secas y la
//  revisión sería «parecen correctos» en vez de una comprobación.
const ENCARGO = `Para quien lea esto (persona o IA): abajo va un vídeo partido en segmentos.
De cada segmento está su transcripción literal y los ejercicios escritos a
partir de ella. Revísalos UNO A UNO y comprueba:

  1. Que la respuesta correcta se deduzca DE LA TRANSCRIPCIÓN DE ESE SEGMENTO
     —ni de otro segmento, ni de conocimiento general.
  2. Que sólo haya una respuesta defendible: los distractores tienen que ser
     claramente falsos, no «menos buenos».
  3. Que la explicación diga por qué, y que no contradiga a la opción marcada
     como correcta.
  4. Que el vocabulario encaje con el nivel declarado del vídeo.
  5. En los de Completar y Drag & Drop: que la palabra correcta aparezca tal
     cual en la transcripción del segmento.
  6. Que no se repita la misma pregunta en dos segmentos distintos.

Contesta con la lista de los que estén mal, diciendo segmento, número de
ejercicio y qué falla. Si no encuentras ninguno, dilo en una línea.`;

/** 0:07 · 2:12 · 1:04:30 — los mismos que enseña el editor. */
export function reloj(segundos) {
    const s = Math.max(0, Math.floor(Number(segundos) || 0));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const r = s % 60;
    return h > 0
        ? `${h}:${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`
        : `${m}:${String(r).padStart(2, '0')}`;
}

const limpio = (v) => (typeof v === 'string' ? v.trim() : '');
const oNada = (v, falta) => limpio(v) || falta;

/**
 * Un ejercicio, escrito entero. Devuelve un array de líneas (sin el cabecero).
 *
 * Recibe `ex.data` tal y como lo guarda la IA o el editor manual. Los seis
 * tipos tienen formas distintas y cada uno se escribe con sus propias palabras:
 * un volcado del JSON serviría para un programador y para nadie más, y esto lo
 * va a leer una IA a la que se le pide un juicio, no un `diff`.
 */
export function describeEjercicio(data) {
    if (!data || typeof data !== 'object') return ['(sin generar)'];
    const fuera = [];
    const tipo = data.type;

    if (tipo === 'quiz') {
        fuera.push(`Pregunta: ${oNada(data.question, '(sin pregunta)')}`);
        const opciones = Array.isArray(data.choices) ? data.choices : [];
        if (!opciones.length) {
            fuera.push('Opciones: (sin opciones)');
        } else {
            fuera.push('Opciones:');
            opciones.forEach((op, i) => {
                //  El índice de la correcta viene de la IA y podría apuntar a
                //  una opción que no existe. Si eso pasa, NINGUNA sale marcada
                //  y se avisa abajo: marcar la primera «por si acaso» daría por
                //  bueno un ejercicio que el alumno no podría acertar.
                const esta = Number(data.correct) === i;
                fuera.push(`  ${LETRAS[i] || i + 1}) ${op}${esta ? '   ← CORRECTA' : ''}`);
            });
            const idx = Number(data.correct);
            if (!Number.isInteger(idx) || idx < 0 || idx >= opciones.length) {
                fuera.push(`  !! La respuesta correcta apunta a la opción ${data.correct}, que no existe.`);
            }
        }
    } else if (tipo === 'truefalse') {
        fuera.push(`Afirmación: ${oNada(data.statement, '(sin afirmación)')}`);
        fuera.push(`Respuesta correcta: ${data.answer === true ? 'VERDADERO'
            : data.answer === false ? 'FALSO' : '(sin respuesta)'}`);
    } else if (tipo === 'fill' || tipo === 'dragdrop') {
        fuera.push(`Plantilla: ${oNada(data.template, '(sin plantilla)')}`);
        const huecos = tipo === 'fill'
            ? (Array.isArray(data.blanks) ? data.blanks : [])
            : (Array.isArray(data.correctWords) ? data.correctWords : []);
        if (!huecos.length) {
            fuera.push('Huecos: (ninguno)');
        } else {
            huecos.forEach((correcta, i) => {
                if (tipo === 'fill') {
                    //  En «Completar», `choices[i]` lleva la correcta MEZCLADA
                    //  con los distractores. Se separa para que se lea quién es
                    //  quién: en el JSON no se distingue, y leerlo mezclado es
                    //  justo lo que hace que un distractor igual de válido pase
                    //  desapercibido.
                    const todas = Array.isArray(data.choices?.[i]) ? data.choices[i] : [];
                    const otras = todas.filter((c) => c !== correcta);
                    fuera.push(`  Hueco ${i + 1}: «${correcta}»`
                        + `   distractores: ${otras.length ? otras.join(', ') : '(ninguno)'}`);
                } else {
                    fuera.push(`  Hueco ${i + 1}: «${correcta}»`);
                }
            });
        }
        if (tipo === 'dragdrop') {
            const d = Array.isArray(data.distractors) ? data.distractors : [];
            fuera.push(`Distractores: ${d.length ? d.join(', ') : '(ninguno)'}`);
        }
        //  Un hueco de más en la plantilla que nadie rellena deja al alumno
        //  delante de un espacio sin respuesta posible.
        const enPlantilla = (limpio(data.template).match(/____/g) || []).length;
        if (enPlantilla !== huecos.length) {
            fuera.push(`  !! La plantilla tiene ${enPlantilla} huecos y hay ${huecos.length} respuestas.`);
        }
    } else if (tipo === 'wordselect') {
        const p = Array.isArray(data.targetWords) ? data.targetWords : [];
        fuera.push(`Palabras que hay que encontrar: ${p.length ? p.join(', ') : '(ninguna)'}`);
    } else if (tipo === 'match') {
        const parejas = Array.isArray(data.pairs) ? data.pairs : [];
        if (!parejas.length) {
            fuera.push('Parejas: (ninguna)');
        } else {
            fuera.push('Parejas:');
            parejas.forEach((p, i) => {
                fuera.push(`  ${i + 1}. ${oNada(p?.left, '(vacío)')}  =  ${oNada(p?.right, '(vacío)')}`);
            });
        }
    } else {
        //  Un tipo que esta función no conoce no se traga en silencio: se dice
        //  cuál es y se vuelca lo que trae, para que el revisor lo vea igual.
        fuera.push(`(tipo desconocido: ${tipo ?? 'sin tipo'})`);
        fuera.push(JSON.stringify(data, null, 2));
    }

    fuera.push(`Explicación: ${oNada(data.explanation, '(sin explicación)')}`);
    return fuera;
}

/**
 * El informe entero, listo para pegar en otra IA.
 *
 * @param {object}   meta       title, channel, level, language, topic, videoId
 * @param {Array}    segments   los del editor: { startTime, endTime, text, exercises[] }
 * @param {object}   etiquetas  { quiz: 'Quiz', ... } — los nombres que ve Rob
 */
export function armaInforme({ meta = {}, segments = [], etiquetas = {} } = {}) {
    const linea = '='.repeat(74);
    const fuera = [];

    let listos = 0;
    let totales = 0;
    for (const s of segments) {
        for (const e of s?.exercises || []) {
            totales += 1;
            if (e?.data) listos += 1;
        }
    }

    fuera.push(linea);
    fuera.push('  VIDEO EDU — EJERCICIOS PARA REVISAR');
    fuera.push(linea);
    fuera.push('');
    fuera.push(ENCARGO);
    fuera.push('');
    fuera.push('-'.repeat(74));
    fuera.push(`Título     : ${oNada(meta.title, '(sin título)')}`);
    fuera.push(`Canal      : ${oNada(meta.channel, '(sin canal)')}`);
    fuera.push(`Nivel      : ${oNada(meta.level, '?')} · ${oNada(meta.language, '?')}`);
    fuera.push(`Tema       : ${oNada(meta.topic, '(sin tema)')}`);
    if (limpio(meta.videoId)) fuera.push(`YouTube    : https://youtu.be/${limpio(meta.videoId)}`);
    //  La cuenta va arriba del todo porque es lo primero que invalida una
    //  revisión: aprobar 26 ejercicios cuando sólo hay 24 escritos.
    fuera.push(`Segmentos  : ${segments.length}`);
    fuera.push(`Ejercicios : ${totales} en total · ${listos} escritos · ${totales - listos} sin generar`);
    fuera.push('-'.repeat(74));

    if (!segments.length) {
        fuera.push('');
        fuera.push('(este vídeo no tiene ningún segmento todavía)');
        return fuera.join('\n');
    }

    segments.forEach((seg, i) => {
        fuera.push('');
        fuera.push(linea);
        fuera.push(`  SEGMENTO #${i + 1}    ${reloj(seg?.startTime)} → ${reloj(seg?.endTime)}`);
        fuera.push(linea);
        //  El rótulo del capítulo, si este fragmento abre uno. No es texto
        //  hablado —es un cartel del panel de YouTube— pero sí dice de qué va
        //  lo que viene, y a quien revise le ayuda a situar la pregunta.
        if (limpio(seg?.titulo)) fuera.push(`CAPÍTULO: ${limpio(seg.titulo)}`);
        fuera.push('TRANSCRIPCIÓN:');
        fuera.push(oNada(seg?.text, '(este segmento no tiene texto)'));

        const ejercicios = Array.isArray(seg?.exercises) ? seg.exercises : [];
        if (!ejercicios.length) {
            fuera.push('');
            fuera.push('(este segmento no tiene ningún ejercicio)');
            return;
        }
        ejercicios.forEach((ex, j) => {
            const nombre = etiquetas[ex?.type] || ex?.type || 'sin tipo';
            fuera.push('');
            fuera.push(`── Ejercicio ${j + 1} · ${nombre} ──`);
            //  El tipo que PIDIÓ Rob y el que DEVOLVIÓ la IA pueden no coincidir
            //  —se pide un quiz y vuelve un verdadero/falso—, y entonces el
            //  alumno recibe otra cosa. Se dice, porque mirando el ejercicio
            //  solo no se nota: los dos están bien escritos.
            if (ex?.data?.type && ex?.type && ex.data.type !== ex.type) {
                fuera.push(`  !! Se pidió «${nombre}» y lo generado es de tipo «${ex.data.type}».`);
            }
            for (const l of describeEjercicio(ex?.data)) fuera.push(l);
        });
    });

    fuera.push('');
    return fuera.join('\n');
}
